/* assets/vc-atelier.js — <vc-atelier> (Task 13b; task-13-brief.md "Atelier module"; prototypes/atelier.html:520-1113). The markup and
 * the CSS are snippets/vc-atelier.liquid; the rules are assets/vc-logic.js (measureProperties, convertMeasure, measureRange, MEASURE_FIELDS).
 *
 * Form (data-mode="form"): the custom-measurements form of the product page.
 *   - Five fields (Bust, Waist, Hip, Skirt length, Height). Focusing one lights its guide on the dress form (activate, retraced
 *     by pathLength); what is typed draws its value on the form (setVal); the copy for the measure is the hint line (each field's static description, in the markup).
 *   - The cm / in toggle converts what is typed (convertMeasure: one decimal, not the prototype's half unit). The line item
 *     always carries cm: hidden inputs `properties[Bust (cm)]` … and `properties[_unit]` hold the value that is sent, and are
 *     disabled, like the typed inputs' `required`, unless setActive(true) (the product page: the Custom variant is chosen).
 *   - validateMeasurements, through measureProperties, is the only validator (MEASURE_RANGES_CM replaces the prototype's RANGE).
 *     A field's message ("Required", "Between 70 and 150 cm") shows under it after its first blur with something in it, or from
 *     the first press of Add to bag (check({ reveal: true }): all of them, and focus goes to the first), and then follows every
 *     keystroke. aria-invalid and aria-describedby (the hint and the message) say the same to a screen reader.
 *   - API for assets/vc-product.js: setActive(on), get valid, check({ reveal }) -> { ok, properties, errors }. It dispatches
 *     `vc:atelier:change` (bubbling) whenever what is typed or its unit changes, so the button can follow.
 *   - The sheet draws itself once (the entrance, atelier.html:611-645) the first time the module is opened and scrolled to;
 *     at 860px and below a small lit copy of the form (.mini, atelier.html:602-609) stays under the header while a field has focus
 *     and the sheet is scrolled away. prefers-reduced-motion: no entrance, no tween, the hint changes at once.
 *
 * Guide (data-mode="guide"): the Custom measurements page (templates/page.custom.json; sections/vc-custom-guide.liquid). The same dress form with
 * its five guides numbered I–V and, beside it, the five measures as a list: the name, how to measure it, and the range accepted (vc-logic
 * measureRange) in the unit chosen. Hovering, focusing or tapping a row lights its guide on the form, through the same activate() as the form's
 * fields (the sheet's, shared: see Sheet); the cm / in toggle converts the ranges. No inputs, nothing is added to the bag (see GuidePart).
 *
 * Timeline (data-mode="timeline"): the made-to-order stitch timeline, standalone (see TimelinePart below).
 *
 * connectedCallback runs again when the element is moved (the theme editor re-renders a section as a new element):
 * https://developer.mozilla.org/en-US/docs/Web/API/Web_components/Using_custom_elements
 */
// '@vc/logic': the import map's name (snippets/scripts.liquid) for the VERSIONED assets/vc-logic.js: a relative import would drop
// this module's ?v= (guard: tests/js/import-map.test.mjs).
import { convertMeasure, measureProperties, measureRange } from '@vc/logic';

const NS = 'http://www.w3.org/2000/svg';
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const f2 = (n) => n.toFixed(2);

/* ---------------------------------------------------------------------------------------------------- the pattern sheet
 * The dress form on its sheet of pattern paper (.sheet), shared by the form (the product page) and the guide (the Custom measurements
 * page): the drawn skirt, the entrance, the mini copy for phones, the cm / in toggle's bar and the lit guides (activate). A part adds what
 * lights with a guide (`fields`, each { name, el }) and what happens in a unit (setUnit). */
class Sheet {
  constructor(el) {
    this.el = el;
    this.unit = 'cm';
    this.opened = false;      // the sheet has been opened once (the entrance runs on the first time)
    this.fields = [];         // what lights with a guide, each { name, el }: the form's fields, the guide's rows
    this.sheet = el.querySelector('.sheet');
    this.form = el.querySelector('.sheet .form');
    this.bar = el.querySelector('.units .bar');
    this.ubtns = [...el.querySelectorAll('.units button')];
    this.drawSkirt();
    this.cloneMini();
    this.collectTrace();
  }

  q(sel, root = this.el) { return root.querySelector(sel); }
  qq(sel, root = this.el) { return [...root.querySelectorAll(sel)]; }

  /* ---------- drawn details: skirt outline, folds, gathers, lace hem (generated so spacing stays even; atelier.html:531-600) ---------- */
  drawSkirt() {
    const K = 7; // folds
    // hem base: quadratic (52,426) (160,456) (268,426) -- front arc of the hem ellipse, CF at y441
    // (ry/rx = 15/108 ~ .14, the same viewing angle as the A/B/C guides), then a soft +-2.5 wave whose
    // low points are the fold crests
    const hemBase = (t) => { const m = 1 - t; return { x: m * m * 52 + 2 * m * t * 160 + t * t * 268, y: m * m * 426 + 2 * m * t * 456 + t * t * 426 }; };
    const hemAt = (t) => {
      const p = hemBase(t), env = Math.pow(Math.sin(Math.PI * t), 0.6);
      p.y += 2.5 * env * Math.cos(2 * Math.PI * (K * t - 0.5));
      return p;
    };
    // bell / cupcake petticoat, left side as C1 Hermite nodes with their handles:
    // waist (120.1,242) -> (76,283) -> tier (51,336) -> widest (45,372, ~68 cm at 3.4 u/cm, ~70% down)
    // -> hem (52,426, tucked in ~6%). The right side is the exact mirror about C.F. x160.
    const SK = [[120.1, 242], [104, 254.5], [86.5, 266.1], [76, 283], [65.7, 299.6], [54.8, 316.8], [51, 336],
      [48.6, 348], [45, 353.2], [45, 372], [45, 390.8], [48.6, 408], [52, 426]];
    const pt = (q, mir) => f2(mir ? 320 - q[0] : q[0]) + ' ' + q[1];
    const S = 140;
    let hem = '', FL = 'M' + pt(SK[0]), FR = '', RD = 'M' + pt(SK[0], 1);
    for (let i = 1; i < SK.length; i += 3) {
      FL += ' C' + pt(SK[i]) + ' ' + pt(SK[i + 1]) + ' ' + pt(SK[i + 2]);
      RD += ' C' + pt(SK[i], 1) + ' ' + pt(SK[i + 1], 1) + ' ' + pt(SK[i + 2], 1);
    }
    for (let i = SK.length - 2; i > 0; i -= 3) FR += ' C' + pt(SK[i], 1) + ' ' + pt(SK[i - 1], 1) + ' ' + pt(SK[i - 2], 1);
    for (let i = 1; i <= S; i++) { const p = hemAt(i / S); hem += ' L' + f2(p.x) + ' ' + f2(p.y); }
    this.q('[data-skirt]').setAttribute('d', FL + hem + FR);
    // right half of the skirt, for the engraved shading clip
    let half = 'M160 242 L' + pt(SK[0], 1) + RD.slice(RD.indexOf(' C'));
    for (let i = S; i >= S / 2; i--) { const p = hemAt(i / S); half += ' L' + f2(p.x) + ' ' + f2(p.y); }
    this.q('[data-shade-skirt]').setAttribute('d', half + ' Z');

    const strokes = (sel, xs, yOf, len, slant) => {
      let d = '';
      xs.forEach((x) => { const y = yOf(x) + 2.5; d += 'M' + x.toFixed(1) + ' ' + y.toFixed(1) + ' l' + ((x - 160) * slant).toFixed(2) + ' ' + len + ' '; });
      const el = document.createElementNS(NS, 'path'); el.setAttribute('d', d); el.setAttribute('class', 'ln');
      this.q(sel).appendChild(el);
    };
    // gathers hang from the band's lower seam: the ellipse arc A47.1 6.5 centred (160,247.5)
    const waistY = (x) => { const u = (x - 160) / 47.1; return 247.5 + 6.5 * Math.sqrt(Math.max(0, 1 - u * u)); };
    const wx = [], tx = [];
    for (let x = 128; x <= 192; x += 8) wx.push(x);
    for (let x = 60; x <= 260; x += 10) tx.push(x);
    strokes('[data-gather="waist"]', wx, waistY, 9, 0.1);
    strokes('[data-gather="tier"]', tx, (x) => { const u = (x - 160) / 109; return 336 + 14 * (1 - u * u); }, 7, 0.05); // = tier seam Q160 364

    // folds: from under the waist gathers to each hem crest, bowing gently outward
    let fd = '';
    for (let i = 0; i < K; i++) {
      const x0 = 128 + (192 - 128) * (i / (K - 1)), y0 = waistY(x0) + 13;
      const e = hemAt((i + 0.5) / K), x1 = e.x, y1 = e.y - 2;
      const side = (x0 + x1) / 2 - 160, sgn = Math.abs(side) < 4 ? 0 : (side > 0 ? 1 : -1);
      const cx = (x0 + x1) / 2 + sgn * 6, cy = (y0 + y1) / 2;
      fd += 'M' + f2(x0) + ' ' + f2(y0) + ' Q' + f2(cx) + ' ' + f2(cy) + ' ' + f2(x1) + ' ' + f2(y1) + ' ';
    }
    const fp = document.createElementNS(NS, 'path'); fp.setAttribute('d', fd); fp.setAttribute('class', 'ln');
    this.q('[data-folds]').appendChild(fp);

    // lace: fine scallops hanging from the waved hem, a row of eyelets inside them
    const N = 28, eyes = this.q('[data-lace-eyes]');
    let d = '';
    for (let i = 0; i < N; i++) {
      const a = hemAt(i / N), b = hemAt((i + 1) / N), r = Math.hypot(b.x - a.x, b.y - a.y) / 2;
      if (i === 0) d += 'M' + f2(a.x) + ' ' + f2(a.y + 1.2);
      d += ' A' + f2(r) + ' ' + f2(r * 0.74) + ' 0 0 0 ' + f2(b.x) + ' ' + f2(b.y + 1.2);
      const m = hemAt((i + 0.5) / N), c = document.createElementNS(NS, 'circle');
      c.setAttribute('cx', f2(m.x)); c.setAttribute('cy', f2(m.y + 4)); c.setAttribute('r', '.8');
      eyes.appendChild(c);
    }
    this.q('[data-lace]').setAttribute('d', d);
  }

  /* ---------- mobile mini sheet: a live copy of the form (same classes, no ids; its paint servers are the original's; atelier.html:602-609) ---------- */
  cloneMini() {
    const mini = this.q('.mini');
    if (!mini || !this.form) return;
    const cl = this.form.cloneNode(true);
    const defs = cl.querySelector('defs'); if (defs) defs.parentNode.removeChild(defs);
    cl.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
    cl.setAttribute('viewBox', '2 10 370 560');
    mini.appendChild(cl);
  }

  /* ---------- the entrance: the sheet's lines trace themselves once (stand, torso, skirt, lace; atelier.html:611-645) ---------- */
  collectTrace() {
    const paths = this.qq('.sheet .form path.ln').filter((p) => !p.classList.contains('cf') && !p.classList.contains('under'));
    this.traced = paths.map((p, i) => { const o = p.closest('[data-o]'); return { p, o: o ? +o.getAttribute('data-o') : 9, i }; })
      .sort((a, b) => (a.o - b.o) || (a.i - b.i)).map((x) => x.p);
  }

  // pathLength dashes are resolved in screen space under non-scaling-stroke (Chrome), which breaks them once the
  // form is drawn above 1px/unit; so while tracing, scale the stroke with the drawing and compensate its width.
  traceTo(v, withTransition) {
    const k = this.form.getBoundingClientRect().height / 560 || 1;
    this.traced.forEach((p, i) => {
      if (!p.style.vectorEffect) {
        const w = parseFloat(getComputedStyle(p).strokeWidth) || 0.8;
        p.style.vectorEffect = 'none'; p.style.strokeWidth = (w / k).toFixed(3);
      }
      p.setAttribute('pathLength', '1');
      p.style.strokeDasharray = '1 1';
      p.style.transition = withTransition ? 'stroke-dashoffset 1400ms cubic-bezier(.7,0,.3,1) ' + (i * 60) + 'ms' : 'none';
      p.style.strokeDashoffset = String(v);
    });
  }

  traceClear() {
    this.traced.forEach((p) => {
      p.removeAttribute('pathLength'); p.style.strokeDasharray = ''; p.style.strokeDashoffset = ''; p.style.transition = '';
      p.style.vectorEffect = ''; p.style.strokeWidth = '';
    });
  }

  entrance() {
    const total = (this.traced.length - 1) * 60 + 1400;
    this.traceTo(1, true);
    requestAnimationFrame(() => requestAnimationFrame(() => { this.traced.forEach((p) => { p.style.strokeDashoffset = '0'; }); }));
    setTimeout(() => this.el.classList.remove('pre'), total * 0.6);
    setTimeout(() => this.traceClear(), total + 60);
  }

  // The sheet draws itself the first time the module is open and 30% of the sheet is on the screen. If that never happens
  // (no IntersectionObserver, or the visitor never scrolls to it) the lines are simply there: `pre` is only ever held for a
  // sheet that is being waited for, and 3s after opening it is played whatever is seen.
  scheduleEntrance() {
    if (this.opened || reducedMotion() || !('IntersectionObserver' in window)) { this.opened = true; return; }
    this.opened = true;
    this.el.classList.add('pre');
    this.traceTo(1, false);
    let done = false;
    const go = () => { if (done) return; done = true; io.disconnect(); clearTimeout(t); setTimeout(() => this.entrance(), 250); };
    const io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) go(); }), { threshold: 0.3 });
    io.observe(this.sheet);
    const t = setTimeout(go, 3000);
  }

  /** The listeners the sheet owns: the unit toggle, the bar on resize, the mini copy's `away`. Returns the `on` helper, which a part uses for its
   *  own listeners and which lets go of them all with destroy(). */
  wire() {
    this.ac = new AbortController();
    const on = (t, type, fn) => t && t.addEventListener(type, fn, { signal: this.ac.signal });
    this.ubtns.forEach((b) => on(b, 'click', () => this.setUnit(b.dataset.u)));
    on(window, 'resize', () => this.placeBar());
    // mobile: the mini sheet shows only once the real sheet has scrolled away
    if ('IntersectionObserver' in window && this.sheet) {
      this.away = new IntersectionObserver((es) => es.forEach((e) => this.el.classList.toggle('away', !e.isIntersecting)), { threshold: 0.35 });
      this.away.observe(this.sheet);
    }
    return on;
  }

  destroy() {
    if (this.ac) this.ac.abort();
    if (this.away) this.away.disconnect();
  }

  /** Light one guide (or none): what lights with it (this.fields: the form's field, the guide's row), the guide, and `data-active` on the module
   *  (atelier.html:700-716). The one place a guide is lit, for the form and for the Custom measurements page. */
  activate(name) {
    this.fields.forEach((f) => f.el.classList.toggle('on', f.name === name));
    this.qq('.g').forEach((g) => {
      const on = g.dataset.g === name;
      if (on && !g.classList.contains('on') && !reducedMotion()) {
        // retrace the guide: reset dash offset, then ease it back in
        const lines = this.qq('.front, .rule', g);
        g.classList.remove('draw');
        lines.forEach((p) => { p.style.strokeDashoffset = '1'; });
        g.getBoundingClientRect();
        g.classList.add('draw');
        requestAnimationFrame(() => { lines.forEach((p) => { p.style.strokeDashoffset = '0'; }); });
      }
      g.classList.toggle('on', on);
    });
    if (name) this.el.setAttribute('data-active', name); else this.el.removeAttribute('data-active');
  }

  /* ---------- the unit toggle (atelier.html:758-778) ---------- */
  placeBar() {
    const b = this.ubtns.find((x) => x.dataset.u === this.unit), host = this.q('.units');
    if (!b || !host || !this.bar) return;
    const hr = host.getBoundingClientRect(), r = b.getBoundingClientRect();
    if (!r.width) return; // not laid out (the module is closed)
    this.bar.style.width = (r.width - 3) + 'px'; // trim trailing letter-spacing
    this.bar.style.transform = 'translateX(' + (r.left - hr.left) + 'px)';
  }

  /** The toggle's state, for a part's setUnit: the unit, the pressed button and the bar under it. */
  pressUnit(u) {
    this.unit = u;
    this.el.dataset.unit = u;
    this.ubtns.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.u === u)));
    this.placeBar();
  }
}

/* ---------------------------------------------------------------------------------------------------- the form */
const KEYS = { bust: 'A', waist: 'B', hip: 'C', skirt: 'D', height: 'E' };   // atelier.html:648
// what each measure asks for is in the markup (atelier.html:649-656; snippets/vc-atelier.liquid), where it is the field's static description
const IDLE = 'Choose a measure and its line is traced on the form.';

class FormPart extends Sheet {
  constructor(el) {
    super(el);
    this.active = false;
    this.submitted = false;   // Add to bag was pressed: every message shows, and follows the typing
    this.sizing = !!(window.CSS && CSS.supports && CSS.supports('field-sizing', 'content'));
    this.fields = [...el.querySelectorAll('.f')].map((f) => ({
      name: f.dataset.f, el: f, input: f.querySelector('input[type="text"]'), cm: f.querySelector('input[data-cm]'),
      err: f.querySelector('[data-err]'), unitText: f.querySelector('[data-vc-unit-text]'), shown: false,
      copy: ((f.querySelector('[data-desc]') || {}).textContent || '').trim(),
    }));
    this.unitField = el.querySelector('[data-unit-field]');
    this.hintP = el.querySelector('.hint p');
    this.setHint(IDLE);
    this.wire();
  }

  /* ---------- measurements ---------- */
  wire() {
    const on = super.wire();
    this.fields.forEach((f, idx) => {
      const { input, name } = f;
      on(input, 'focus', () => { this.activate(name); this.setHint(f.copy, KEYS[name]); });
      on(input, 'input', () => {
        const v = input.value, s = v.replace(/[^0-9.,]/g, '').slice(0, 5);
        if (s !== v) {
          const pos = Math.max(0, (input.selectionStart || 0) - (v.length - s.length));
          input.value = s;
          try { input.setSelectionRange(pos, pos); } catch (_) { /* not a text control that takes a selection */ }
        }
        this.setVal(name, input.value ? input.value.replace(',', '.') + ' ' + this.unit : '');
        this.sizeInput(input);
        this.changed();
      });
      on(input, 'keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const nx = this.fields[idx + 1];
        if (nx) { nx.input.focus(); return; }
        // the last field: Enter adds the piece when the form is right, else shows what is not
        const form = this.el.closest('form');
        if (form && typeof form.requestSubmit === 'function') form.requestSubmit(); else this.check({ reveal: true });
      });
      on(input, 'blur', () => {
        if (input.value.trim()) f.shown = true;   // a field with something in it is judged once it is left
        this.paint();
        setTimeout(() => {
          if (this.el.contains(document.activeElement) && document.activeElement.tagName === 'INPUT') return;
          this.activate(null);
          this.setHint(IDLE);
        }, 30);
      });
    });
    this.qq('.f input[type="text"]').forEach((i) => this.sizeInput(i));
  }

  destroy() {
    super.destroy();
    clearTimeout(this.hintT);
  }

  values() {
    const v = {};
    this.fields.forEach((f) => { v[f.name] = f.input.value; });
    return v;
  }

  sizeInput(inp) { if (!this.sizing) inp.style.width = ((inp.value.length || 1) + 0.1) + 'ch'; }

  setVal(name, text) { this.qq(`.g[data-g="${name}"] .val`).forEach((v) => { v.textContent = text; }); }

  setHint(text, key, warn) {
    const p = this.hintP;
    if (!p) return;
    const html = (key ? '<span class="k">' + key + '</span>' : '') + text;
    if (p.innerHTML === html && p.classList.contains('warn') === !!warn) return;
    p.classList.add('out');
    clearTimeout(this.hintT);
    this.hintT = setTimeout(() => {
      p.innerHTML = html;
      p.classList.toggle('warn', !!warn);
      p.classList.remove('out');
    }, reducedMotion() ? 0 : 260);
  }

  /** The messages, from validateMeasurements: shown for a field once it has been left with something in it or Add to bag has
   *  been pressed; the guide and the row follow (atelier.html:679-684 setWarn). Also the row's "valid" thread and the hidden cm inputs. */
  paint() {
    const r = measureProperties(this.values(), this.unit);
    this.fields.forEach((f) => {
      const msg = r.errors[f.name] || '';
      const show = (this.submitted || f.shown) && !!msg;
      const has = f.input.value.trim() !== '';
      if (f.err.textContent !== (show ? msg : '')) f.err.textContent = show ? msg : '';
      f.el.classList.toggle('warn', show);
      f.el.classList.toggle('valid', has && !msg);
      this.qq(`.g[data-g="${f.name}"]`).forEach((g) => g.classList.toggle('warn', show));
      if (show) f.input.setAttribute('aria-invalid', 'true'); else f.input.removeAttribute('aria-invalid');
      f.cm.value = r.properties ? r.properties[f.cm.name.slice(11, -1)] : '';
    });
    this.unitField.value = this.unit;
    return r;
  }

  changed() {
    this.paint();
    this.el.dispatchEvent(new CustomEvent('vc:atelier:change', { bubbles: true, detail: { valid: this.valid, unit: this.unit } }));
  }

  setUnit(u) {
    if (u === this.unit || (u !== 'cm' && u !== 'in')) return;
    this.fields.forEach((f) => { f.input.value = convertMeasure(f.input.value, this.unit, u); this.sizeInput(f.input); });
    this.pressUnit(u);
    this.fields.forEach((f) => {
      f.unitText.textContent = u;
      this.setVal(f.name, f.input.value ? f.input.value.replace(',', '.') + ' ' + u : '');
    });
    this.changed();
  }

  /* ---------- for assets/vc-product.js ---------- */
  get valid() { return measureProperties(this.values(), this.unit).ok; }

  /** The form's state; with `reveal`, every message is shown from now on and focus goes to the first field that is wrong. */
  check({ reveal = false } = {}) {
    if (reveal) this.submitted = true;
    const r = this.paint();
    if (reveal && !r.ok) {
      const first = this.fields.find((f) => r.errors[f.name]);
      if (first) first.input.focus();
    }
    return { ok: r.ok, properties: r.properties, errors: r.errors };
  }

  /** The module is open for a Custom measurements variant: its fields are required and its hidden inputs are sent. */
  setActive(on) {
    on = !!on;
    if (on === this.active) return;
    this.active = on;
    this.fields.forEach((f) => { f.input.required = on; f.cm.disabled = !on; });
    this.unitField.disabled = !on;
    if (on) {
      requestAnimationFrame(() => { this.placeBar(); this.scheduleEntrance(); });
    } else {
      this.activate(null);
      this.setHint(IDLE);
    }
    this.paint();
  }
}

/* ---------------------------------------------------------------------------------------------------- the guide
 * The Custom measurements page: the sheet with the five guides numbered I–V, and the five measures as a list (snippets/vc-atelier.liquid, mode
 * 'guide'). A row is a button: hovering it (a mouse), focusing it or tapping it lights its guide on the form, with the sheet's own activate().
 * A focus or a tap holds the row lit: a finger has no pointer to leave with, so a tap stays until another row is tapped, focus leaves the
 * list, or the list is out of sight. Under 860px the mini copy of the form (Sheet) shows under the header while a row is lit and the real sheet is
 * scrolled away. The cm / in toggle converts the ranges (measureRange: the figures the form's own messages give). */
class GuidePart extends Sheet {
  constructor(el) {
    super(el);
    this.held = null;   // the row a focus or a tap holds lit
    this.fields = this.qq('.gd-item').map((li) => ({ name: li.dataset.g, el: li, btn: this.q('.gd-row', li), lohi: this.q('.gd-lohi', li), u: this.q('.gd-u', li) }));
    this.paintRanges();
    this.wire();
    requestAnimationFrame(() => { this.placeBar(); this.scheduleEntrance(); });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.placeBar());   // the toggle's words change width when the fonts come
  }

  wire() {
    const on = super.wire();
    this.fields.forEach((f) => {
      const hold = () => { this.held = f.name; this.activate(f.name); };
      on(f.btn, 'pointerenter', (e) => { if (e.pointerType !== 'touch') this.activate(f.name); });
      on(f.btn, 'pointerleave', (e) => { if (e.pointerType !== 'touch') this.activate(this.held); });
      on(f.btn, 'focus', hold);
      on(f.btn, 'click', hold);
      on(f.btn, 'blur', () => setTimeout(() => {
        if (this.fields.some((x) => x.btn === document.activeElement)) return;   // focus went to another row
        this.held = null;
        this.activate(null);
      }, 30));
    });
    const list = this.q('.gd-list');
    if (list && 'IntersectionObserver' in window) {
      this.listIo = new IntersectionObserver((es) => es.forEach((e) => { if (!e.isIntersecting && this.held) { this.held = null; this.activate(null); } }));
      this.listIo.observe(list);
    }
  }

  destroy() {
    super.destroy();
    if (this.listIo) this.listIo.disconnect();
  }

  /** Each row's range in the unit shown, from vc-logic (the one table of ranges): "65–140" and "cm". */
  paintRanges() {
    this.fields.forEach((f) => {
      const [lo, hi] = measureRange(f.name, this.unit);
      f.lohi.textContent = lo + '–' + hi;
      f.u.textContent = this.unit;
    });
  }

  setUnit(u) {
    if (u === this.unit || (u !== 'cm' && u !== 'in')) return;
    this.pressUnit(u);
    this.paintRanges();
  }
}

/* ---------------------------------------------------------------------------------------------------- the timeline
 * The made-to-order stitch timeline (Task 13b; atelier.html:454-515 markup, 780-1044 script). One thread is sewn across the page
 * like a hem, through five stations (Ordered, Pattern & cut, Sewn, Hand-finished, Dispatched):
 *   - layout(): the stitches follow the stations' real positions, so the same markup lays out across a desktop and down a phone
 *     (the thread turns vertical under 760px, atelier.html:241-247). A ResizeObserver re-lays it out, keeping the progress.
 *   - sew(): once, when 30% of it is on the screen, the needle runs the thread in 4.2s (the eased progress reveals the stitch mask,
 *     and each station lights, with a pulse, as the needle reaches it).
 *   - the sheen: candlelight travelling along the finished thread (atelier.html:899-1044). `data-sheen="visible"` (the default, the
 *     operator's 流光) loops while the timeline is in view, brighter and wider: a pass of 4.6s, 6.5s of rest between them. "subtle" is the
 *     restrained finishing moment: three passes, 28s of rest, and an extra pass on returning after 10s away (BRIEF 4.4: ambient
 *     loops are slow or none). Between passes no requestAnimationFrame runs: a setTimeout sleeps out the rest, and a pause (off screen,
 *     a hidden tab) banks the rest already served. Each eyelet glints as the light passes, and the dispatch window takes a warm tint
 *     on the first pass.
 *   - the dates are the visitor's own: "Today, 30 Sep" and "Est. 14 Oct – 28 Oct" (2-4 weeks from today).
 *   - prefers-reduced-motion: the finished thread and the gold last label, no sewing, no sheen, no pulse.
 * The paint servers are the snippet's fixed ids (vcTl*); the section that renders it has `limit: 1`.
 */
// a small seeded generator (mulberry32), so the stitches are the same on every page view
function rng(seed) {
  return function () {
    seed |= 0; seed = seed + 0x6D2B79F5 | 0;
    let t = Math.imul(seed ^ seed >>> 15, 1 | seed);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

// cubic-bezier as an easing function (Newton iterations on x)
function bez(x1, y1, x2, y2) {
  const c = (t, p1, p2) => 3 * p1 * t * (1 - t) * (1 - t) + 3 * p2 * t * t * (1 - t) + t * t * t;
  const dc = (t, p1, p2) => 3 * p1 * (1 - t) * (1 - t) + 6 * (p2 - p1) * t * (1 - t) + 3 * (1 - p2) * t * t;
  return (x) => {
    let t = x;
    for (let i = 0; i < 6; i++) { const d = dc(t, x1, x2); if (Math.abs(d) < 1e-6) break; t -= (c(t, x1, x2) - x) / d; }
    return c(Math.max(0, Math.min(1, t)), y1, y2);
  };
}

// one straight, tapered stitch: 1.3 wide mid, .4 at the ends (cubic control h gives mid half-width w/2)
function stitch(cx, cy, a, L, w) {
  const h = (4 * w - 0.4) / 6, l = L / 2, ca = Math.cos(a), sa = Math.sin(a);
  const P = (x, y) => f2(cx + x * ca - y * sa) + ' ' + f2(cy + x * sa + y * ca);
  return 'M' + P(-l, -0.2) + 'C' + P(-l / 3, -h) + ' ' + P(l / 3, -h) + ' ' + P(l, -0.2) + 'L' + P(l, 0.2) + 'C' + P(l / 3, h) + ' ' + P(-l / 3, h) + ' ' + P(-l, 0.2) + 'Z';
}

const SEW_MS = 4200;
const SEW_EASE = bez(0.55, 0, 0.25, 1);
const SH_PASS = 4600, SH_LEAD = 900, SH_AWAY = 10000;
const SH_EASE = bez(0.42, 0, 0.58, 1);

class TimelinePart {
  constructor(el) {
    this.el = el;
    this.tl = this.q('.tl');
    this.svg = this.q('.thread', this.tl);
    this.chalk = this.q('.chalk', this.svg);
    this.sts = this.qq('.st', this.svg);
    this.sh = this.q('.sh', this.svg);
    this.maskP = this.q('mask .m', this.svg);
    this.needle = this.q('.needle', this.svg);
    this.items = this.qq('li', this.tl);
    this.chalkG = this.q('[data-chalk]', this.svg);
    this.pts = []; this.fr = []; this.total = 0; this.progress = 0;
    this.sewing = false;

    // sheen (atelier.html:912-929): two strengths; `visible` loops while in view, brighter and wider
    this.vis = el.dataset.sheen !== 'subtle';
    this.rest = this.vis ? 6500 : 28000;
    this.max = this.vis ? Infinity : 3;
    this.grad = this.q('[data-sheen-grad]', this.svg);
    this.filt = this.q('[data-bloom]', this.svg);
    this.core = this.q('.sc', this.svg);
    this.bloom = this.q('.sb', this.svg);
    this.lab = this.q('.wh[data-w="window"]', this.tl);
    this.gl = this.qq('.eye', this.tl).map((e) => ({ g: this.q('.gl', e), a: this.q('.ga', e), v: 0 }));
    if (this.vis) {
      el.classList.add('sheen-visible');
      // stop opacities for offsets 0/.26/.44/.5/.56/.74/1, plus a warmer, whiter core
      [0, 0.5, 0.85, 1, 0.85, 0.5, 0].forEach((o, i) => { const s = this.grad.children[i]; if (s) s.setAttribute('stop-opacity', o); });
      if (this.grad.children[3]) this.grad.children[3].style.setProperty('stop-color', 'var(--vca-sheen-core)');
      const blur = this.filt.querySelector('feGaussianBlur'); if (blur) blur.setAttribute('stdDeviation', '2.6');
    }
    this.shn = { horiz: true, a0: 0, a1: 0, W: 100, st: [], lab: null, started: false, run: false, seen: true, phase: 'off', clock: 0, last: null,
      raf: 0, wait: 0, waitT0: 0, timer: 0, passes: 0, bonus: false, labDone: false, lit: false, awayAt: 0 };
    this.dates();
    this.wire();
  }

  q(sel, root = this.el) { return root.querySelector(sel); }
  qq(sel, root = this.el) { return [...root.querySelectorAll(sel)]; }

  /* ---------- the making: dates (atelier.html:780-788) ---------- */
  dates() {
    const today = new Date();
    const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
    const dm = (d) => d.getDate() + ' ' + d.toLocaleDateString('en-GB', { month: 'short' });
    const da = addDays(today, 14), db = addDays(today, 28);
    const win = da.getMonth() === db.getMonth() ? da.getDate() + ' – ' + dm(db) : dm(da) + ' – ' + dm(db);
    const t = this.q('.wh[data-w="today"]', this.tl);
    if (t) t.textContent = 'Today, ' + dm(today);
    if (this.lab) { this.lab.textContent = 'Est. ' + win; this.lab.setAttribute('data-t', 'Est. ' + win); }   // data-t: the text of the sheen overlay (::after)
  }

  wire() {
    this.ac = new AbortController();
    const sig = { signal: this.ac.signal };
    document.addEventListener('visibilitychange', () => this.sheenWake(), sig);
    // pause off-screen and in a hidden tab; a long absence earns one extra pass on return (subtle mode)
    if ('IntersectionObserver' in window) {
      this.seenIo = new IntersectionObserver((es) => {
        const shn = this.shn;
        es.forEach((e) => {
          const was = shn.seen; shn.seen = e.isIntersecting;
          if (was && !shn.seen) shn.awayAt = performance.now();
          else if (!was && shn.seen && shn.passes > 0 && shn.awayAt && performance.now() - shn.awayAt > SH_AWAY && (shn.phase === 'wait' || shn.phase === 'off')) {
            clearTimeout(shn.timer); shn.timer = 0;
            shn.bonus = true; shn.run = true; shn.phase = 'wait'; shn.wait = SH_LEAD;
          }
        });
        this.sheenWake();
      }, { threshold: 0 });
      this.seenIo.observe(this.tl);
    }
    // the stitches follow the stations: laid out again whenever the timeline changes size (a resize, the fonts arriving)
    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(() => { cancelAnimationFrame(this.roRaf); this.roRaf = requestAnimationFrame(() => this.layout()); });
      this.ro.observe(this.tl);
    } else {
      window.addEventListener('resize', () => this.layout(), sig);
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.layout());
    this.layout();
    this.start();
  }

  destroy() {
    if (this.ac) this.ac.abort();
    if (this.ro) this.ro.disconnect();
    if (this.seenIo) this.seenIo.disconnect();
    if (this.startIo) this.startIo.disconnect();
    cancelAnimationFrame(this.roRaf); cancelAnimationFrame(this.sewRaf); cancelAnimationFrame(this.shn.raf);
    clearTimeout(this.shn.timer); clearTimeout(this.startT);
    this.shn.raf = 0; this.shn.timer = 0;
  }

  /** The thread is sewn once, when 30% of it is on the screen; with reduced motion (or no IntersectionObserver) it is simply there. */
  start() {
    if (this.started) return;
    this.started = true;
    if (reducedMotion() || !('IntersectionObserver' in window)) { this.render(1, true); this.sheenOff(); return; }
    this.render(0, true);
    this.startIo = new IntersectionObserver((es) => {
      if (!es.some((e) => e.isIntersecting)) return;
      this.startIo.disconnect();
      this.startT = setTimeout(() => this.sew(), 250);
    }, { threshold: 0.3 });
    this.startIo.observe(this.el);
  }

  /* ---------- the running stitch (atelier.html:790-871) ---------- */
  layout() {
    const host = this.tl.getBoundingClientRect(), rr = this.el.getBoundingClientRect();
    if (!host.width) return;   // not laid out (display: none)
    this.pts = this.qq('.eye', this.tl).map((e) => {
      const r = e.getBoundingClientRect();
      return { x: r.left - host.left + r.width / 2, y: r.top - host.top + r.height / 2 };
    });
    const pts = this.pts;
    const rnd = rng(7), J = (k) => (rnd() * 2 - 1) * k;
    const gap = 9.5, PITCH = 10;
    let d = '', full = 'M' + f2(pts[0].x) + ' ' + f2(pts[0].y);
    const dd = ['', '', ''];
    this.total = 0; this.fr = [0];
    for (let i = 0; i < pts.length - 1; i++) {
      const p = pts[i], q = pts[i + 1], dx = q.x - p.x, dy = q.y - p.y, L = Math.sqrt(dx * dx + dy * dy);
      const ux = dx / L, uy = dy / L, ang = Math.atan2(dy, dx);
      const run = L - 2 * gap, n = Math.max(1, Math.floor((run + 4) / PITCH)), pad = (run - (n * PITCH - 4)) / 2;
      for (let k = 0; k < n; k++) {
        const s = gap + pad + k * PITCH + 3 + J(0.3), off = J(0.15);
        const one = stitch(p.x + ux * s - uy * off, p.y + uy * s + ux * off, ang + J(1.5) * Math.PI / 180, 6 + J(0.6), 1.3 + J(0.15));
        d += one; dd[Math.floor(rnd() * 3)] += one;
      }
      full += ' L' + f2(q.x) + ' ' + f2(q.y);
      this.total += L; this.fr.push(this.total);
    }
    this.fr = this.fr.map((v) => v / this.total);
    this.sts.forEach((el, j) => el.setAttribute('d', dd[j]));
    this.sh.setAttribute('d', d);
    this.maskP.setAttribute('d', full);
    this.maskP.style.strokeDasharray = this.total + ' ' + this.total;
    // chalk: horizontal hem runs edge to edge of the module; vertical (mobile) only between stations
    const horiz = Math.abs(pts[pts.length - 1].x - pts[0].x) > Math.abs(pts[pts.length - 1].y - pts[0].y);
    if (horiz) {
      const x0 = rr.left - host.left, x1 = rr.right - host.left;
      this.chalk.setAttribute('d', 'M' + f2(x0) + ' ' + f2(pts[0].y) + ' L' + f2(x1) + ' ' + f2(pts[0].y));
      this.chalkG.setAttribute('x1', f2(x0)); this.chalkG.setAttribute('x2', f2(x1)); this.chalkG.setAttribute('y1', '0'); this.chalkG.setAttribute('y2', '0');
    } else {
      this.chalk.setAttribute('d', full);
      this.chalkG.setAttribute('x1', '0'); this.chalkG.setAttribute('x2', '0');
      this.chalkG.setAttribute('y1', f2(pts[0].y - 20)); this.chalkG.setAttribute('y2', f2(pts[pts.length - 1].y + 20));
    }
    this.render(this.progress, true);
    this.sheenLayout(d, horiz);
  }

  pointAt(t) {
    let dist = t * this.total;
    const pts = this.pts;
    for (let i = 0; i < pts.length - 1; i++) {
      const p = pts[i], q = pts[i + 1], L = Math.hypot(q.x - p.x, q.y - p.y);
      if (dist <= L || i === pts.length - 2) {
        const k = Math.max(0, Math.min(1, dist / L));
        return { x: p.x + (q.x - p.x) * k, y: p.y + (q.y - p.y) * k, a: Math.atan2(q.y - p.y, q.x - p.x) };
      }
      dist -= L;
    }
    return { x: 0, y: 0, a: 0 };
  }

  pulse(li) {
    li.classList.remove('pulse'); void li.offsetWidth; li.classList.add('pulse');
    setTimeout(() => li.classList.remove('pulse'), 800);
  }

  render(t, quiet) {
    this.progress = t;
    if (!this.pts.length) return;
    this.maskP.style.strokeDashoffset = String(this.total * (1 - t));
    const at = this.pointAt(t);
    this.needle.setAttribute('transform', 'translate(' + f2(at.x) + ' ' + f2(at.y) + ') rotate(' + f2(at.a * 180 / Math.PI + 3) + ')');
    this.needle.classList.toggle('on', t > 0.0005 && t < 0.995);
    this.items.forEach((li, i) => {
      const on = t > 0.0005 && t >= this.fr[i] - 0.002;
      if (on && !li.classList.contains('lit') && !quiet && !reducedMotion()) this.pulse(li);
      li.classList.toggle('lit', on);
    });
  }

  sew() {
    if (this.sewing) return;
    if (reducedMotion()) { this.render(1, true); this.sheenOff(); return; }
    this.sewing = true;
    let t0 = null;
    const step = (ts) => {
      if (t0 === null) t0 = ts;
      const k = Math.min(1, (ts - t0) / SEW_MS);
      this.render(k === 0 ? 0.001 : SEW_EASE(k));
      if (k < 1) this.sewRaf = requestAnimationFrame(step); else { this.sewing = false; this.sheenStart(); }
    };
    this.sewRaf = requestAnimationFrame(step);
  }

  /* ---------- the making: sheen (candlelight along the finished thread; atelier.html:899-1044) ----------
     A light of half-width W travels the thread axis from before station I to past station V:
       c(u) = a0 - W + u * (a1 - a0 + 2W),  u = ease(t / PASS)
     The stitch path is repainted with a gradient centred on c (so only stitch shapes are lit) and a lightly blurred copy gives a
     1-2px halo. Each eyelet glints with proximity exp(-((c - s_i)/sig)^2), held by an exponential decay so the catch-light lingers
     after the light. Cadence (BRIEF 4.4: ambient loops >= 30s or none, but the operator asked for a visible 流光): "visible" loops
     while in view; "subtle" is a finishing moment of at most three passes. */
  sheenLayout(d, horiz) {
    const shn = this.shn, pts = this.pts;
    this.core.setAttribute('d', d); this.bloom.setAttribute('d', d);
    shn.horiz = horiz;
    const ax = horiz ? 'x' : 'y';
    shn.st = pts.map((p) => p[ax]);
    shn.a0 = shn.st[0]; shn.a1 = shn.st[shn.st.length - 1];
    const span = shn.a1 - shn.a0;
    // desktop: ~107px half-width; mobile (vertical, ~250px thread): a short run of ~40px travels down
    shn.W = (horiz ? Math.max(60, Math.min(118, span * 0.105)) : Math.max(36, span * 0.16)) * (this.vis ? 1.45 : 1);
    // bloom filter region: the thread's box plus room for the blur
    const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
    const x0 = Math.min(...xs) - 12, y0 = Math.min(...ys) - 12;
    this.filt.setAttribute('x', f2(x0)); this.filt.setAttribute('y', f2(y0));
    this.filt.setAttribute('width', f2(Math.max(...xs) - x0 + 12)); this.filt.setAttribute('height', f2(Math.max(...ys) - y0 + 12));
    // the dispatch label, relative to the timeline box
    if (this.lab) {
      const host = this.tl.getBoundingClientRect(), r = this.lab.getBoundingClientRect();
      shn.lab = { l: r.left - host.left, t: r.top - host.top, w: r.width, h: r.height };
      this.lab.style.setProperty('--lw', f2(Math.max(36, r.width * 0.55)) + 'px'); // wide: a tint that rolls, not a glint
    }
  }

  setGrad(c, W) {
    const g = this.grad;
    if (this.shn.horiz) { g.setAttribute('x1', f2(c - W)); g.setAttribute('x2', f2(c + W)); g.setAttribute('y1', '0'); g.setAttribute('y2', '0'); }
    else { g.setAttribute('y1', f2(c - W)); g.setAttribute('y2', f2(c + W)); g.setAttribute('x1', '0'); g.setAttribute('x2', '0'); }
  }

  // draw the light at eased position u (0..1); dt > 0 lets glints decay, dt = 0 renders a frozen frame;
  // lab lets the tint cross the dispatch label (first pass only)
  sheenAt(u, dt, lab) {
    const shn = this.shn, W = shn.W, c = shn.a0 - W + u * (shn.a1 - shn.a0 + 2 * W), sig = W * 0.34;
    this.setGrad(c, W);
    this.gl.forEach((o, i) => {
      const z = (c - shn.st[i]) / sig, prox = Math.exp(-z * z);
      o.v = dt > 0 ? Math.max(prox, o.v * Math.exp(-dt / 320)) : prox;
      o.g.style.opacity = o.v < 0.004 ? '0' : f2(o.v * 0.95);
      // the catch-light slides round the ring as the light goes by
      o.a.setAttribute('transform', 'rotate(' + f2(Math.max(-1.6, Math.min(1.6, z)) * 34) + ')');
    });
    if (!this.lab || !shn.lab) return;
    const L = shn.lab, lw = parseFloat(this.lab.style.getPropertyValue('--lw')) || 40;
    let q = -1;
    if (lab) {
      if (shn.horiz) q = (c - (L.l - lw / 2)) / (L.w + lw);
      else q = (c - (shn.a1 - W * 0.6)) / (W * 1.6);
    }
    this.lab.style.setProperty('--lx', (q < -0.05 || q > 1.05) ? '-999px' : f2(-lw + Math.max(0, Math.min(1, q)) * (L.w + lw)) + 'px');
  }

  sheenOff() {
    this.setGrad(-9999, 1);
    this.gl.forEach((o) => { o.v = 0; o.g.style.opacity = '0'; });
    if (this.lab) this.lab.style.setProperty('--lx', '-999px');
  }

  sheenActive() { return this.shn.run && this.shn.seen && !document.hidden; }

  sheenTick(ts) {
    const shn = this.shn;
    shn.raf = 0;
    if (!this.sheenActive()) { shn.last = null; return; }
    const dt = shn.last === null ? 16 : Math.min(64, ts - shn.last);
    shn.last = ts;
    if (shn.phase === 'pass') {
      shn.clock += dt;
      if (shn.clock < SH_PASS) this.sheenAt(SH_EASE(shn.clock / SH_PASS), dt, shn.lit);
      else { this.sheenAt(1, dt, false); shn.phase = 'tail'; }
    } else if (shn.phase === 'tail') {
      // after the pass: let the last glints fade, then go dark and sleep
      this.sheenAt(1, dt, false);
      if (this.gl.every((o) => o.v < 0.004)) { this.sheenOff(); this.sheenDone(); return; }
    } else return;
    shn.raf = requestAnimationFrame((t) => this.sheenTick(t));
  }

  // a pass has finished: count it (an extra pass on return does not count) and rest or stop
  sheenDone() {
    const shn = this.shn;
    if (shn.bonus) shn.bonus = false; else shn.passes++;
    if (shn.passes >= this.max) { shn.phase = 'off'; shn.run = false; return; }
    this.sheenSleep(this.rest);
  }

  sheenSleep(ms) {
    const shn = this.shn;
    shn.phase = 'wait'; shn.wait = ms; clearTimeout(shn.timer); shn.timer = 0;
    this.sheenWake();
  }

  sheenGo() {
    const shn = this.shn;
    shn.timer = 0;
    if (!this.sheenActive()) return;
    shn.phase = 'pass'; shn.clock = 0; shn.last = null;
    shn.lit = !shn.labDone; shn.labDone = true;
    shn.raf = requestAnimationFrame((t) => this.sheenTick(t));
  }

  // resume or pause whatever phase we are in; the wait only counts time on screen
  sheenWake() {
    const shn = this.shn;
    if (!this.sheenActive()) {
      if (shn.timer) { clearTimeout(shn.timer); shn.timer = 0; shn.wait -= performance.now() - shn.waitT0; }
      if (shn.raf) { cancelAnimationFrame(shn.raf); shn.raf = 0; }
      shn.last = null;
      return;
    }
    if (shn.phase === 'wait') {
      if (!shn.timer) { shn.waitT0 = performance.now(); shn.timer = setTimeout(() => this.sheenGo(), Math.max(0, shn.wait)); }
    } else if ((shn.phase === 'pass' || shn.phase === 'tail') && !shn.raf) {
      shn.last = null; shn.raf = requestAnimationFrame((t) => this.sheenTick(t));
    }
  }

  sheenStart(lead) {
    const shn = this.shn;
    if (reducedMotion()) { this.sheenOff(); return; } // reduced motion: no sheen; the finished thread and gold label carry the ending
    if (shn.started) return;
    shn.started = shn.run = true;
    this.sheenSleep(lead == null ? SH_LEAD : lead);
  }
}

/* ---------------------------------------------------------------------------------------------------- the element */
export class VcAtelier extends HTMLElement {
  connectedCallback() {
    if (this.impl) { if (this.impl.wire) this.impl.wire(); return; }   // moved in the page: disconnectedCallback let go of the listeners
    const Part = { timeline: TimelinePart, guide: GuidePart }[this.dataset.mode] || FormPart;
    this.impl = new Part(this);
  }

  disconnectedCallback() { if (this.impl && this.impl.destroy) this.impl.destroy(); }

  // the form's API (assets/vc-product.js). The part is kept in `impl`: an element's own `part` is a DOMTokenList (CSS Shadow Parts),
  // whose setter would turn the object into the string "[object Object]" in the part attribute.
  setActive(on) { if (this.impl && this.impl.setActive) this.impl.setActive(on); }
  get valid() { return !!(this.impl && this.impl.valid); }
  check(opts) { return this.impl && this.impl.check ? this.impl.check(opts) : { ok: false, properties: null, errors: {} }; }
}

if (!customElements.get('vc-atelier')) customElements.define('vc-atelier', VcAtelier);
